import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState, useCallback, useEffect, useRef } from "react";
import { Card, Text, Badge, Button, Spinner, Input, Field, Dropdown, Option, Table, TableHeader, TableHeaderCell, TableBody, TableRow, TableCell, MessageBar, MessageBarBody, } from "@fluentui/react-components";
import { TagRegular, ArrowSyncRegular, SendRegular, CheckmarkCircleRegular, ErrorCircleRegular, ClockRegular, } from "@fluentui/react-icons";
import { useAuth } from "../../../context/AuthContext";
import { PeoplePicker } from "../../common/PeoplePicker";
import { getBadgeEnrollements, createBadgeEnrollement } from "../../../services/dataService";
const HORAIRES_DEFAUT = [
    "HORAIRE 35H",
    "HORAIRE 39H",
    "HORAIRE 28H",
    "COMITE",
    "NON BADGEANT",
];
const PROFILS_DEFAUT = [
    "Badger ses entrées/sorties",
    "NON BADGEUR",
];
export const BadgeEnrollementView = () => {
    const { apiToken, graphToken, userEmail } = useAuth();
    const accessToken = apiToken;
    const [enrollements, setEnrollements] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);
    // Formulaire d'enrôlement
    const [selectedUser, setSelectedUser] = useState(null);
    const [badgeUid, setBadgeUid] = useState("");
    const [horaire, setHoraire] = useState("HORAIRE 35H");
    const [profil, setProfil] = useState("Badger ses entrées/sorties");
    const [serviceCible, setServiceCible] = useState("");
    const [managerEmail, setManagerEmail] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const badgeInputRef = useRef(null);
    const showMsg = (msg, isError = false) => {
        if (isError) {
            setError(msg);
            setSuccess(null);
        }
        else {
            setSuccess(msg);
            setError(null);
        }
        setTimeout(() => { setSuccess(null); setError(null); }, 4000);
    };
    const fetchData = useCallback(async () => {
        if (!accessToken)
            return;
        setLoading(true);
        try {
            const data = await getBadgeEnrollements(accessToken);
            setEnrollements(data);
        }
        catch (err) {
            showMsg(err instanceof Error ? err.message : "Erreur", true);
        }
        finally {
            setLoading(false);
        }
    }, [accessToken]);
    useEffect(() => { fetchData(); }, [fetchData]);
    // Quand on sélectionne un salarié, récupérer ses infos Entra pour pré-remplir
    const handleUserSelect = async (user) => {
        setSelectedUser(user);
        setBadgeUid("");
        setTimeout(() => badgeInputRef.current?.focus(), 100);
        // Récupérer le département et le manager depuis Graph (si token dispo)
        if (!graphToken)
            return;
        try {
            const res = await fetch(`https://graph.microsoft.com/beta/users/${encodeURIComponent(user.mail)}?$select=department,manager&$expand=manager($select=mail,displayName)`, { headers: { Authorization: `Bearer ${graphToken}` } });
            if (!res.ok)
                return;
            const data = await res.json();
            if (data.department)
                setServiceCible(data.department);
            if (data.manager?.mail)
                setManagerEmail(data.manager.mail);
        }
        catch { /* silent */ }
    };
    const handleBadgeScan = (value) => {
        // Nettoyer le UID (supprimer espaces, retours chariot du lecteur)
        const clean = value.trim().replace(/[\r\n]/g, "");
        setBadgeUid(clean);
    };
    const handleSubmit = async () => {
        if (!accessToken || !selectedUser || !badgeUid) {
            showMsg("Sélectionnez un salarié et scannez un badge", true);
            return;
        }
        setSubmitting(true);
        try {
            const res = await createBadgeEnrollement(accessToken, {
                employeeEmail: selectedUser.mail,
                employeeName: selectedUser.displayName,
                employeeEntraId: selectedUser.id,
                badgeUid,
                serviceCible,
                horaireCible: horaire,
                profilBadgeage: profil,
                managerEmail,
            });
            if (res.duplicate) {
                showMsg(`Une demande est déjà en attente pour ${selectedUser.displayName}`, true);
            }
            else {
                showMsg(`Badge ${badgeUid} enregistré pour ${selectedUser.displayName}. Il sera créé dans Kelio dans quelques minutes.`);
                // Reset
                setSelectedUser(null);
                setBadgeUid("");
                setServiceCible("");
                setManagerEmail("");
                await fetchData();
            }
        }
        catch (err) {
            showMsg(err instanceof Error ? err.message : "Erreur", true);
        }
        finally {
            setSubmitting(false);
        }
    };
    // Capture automatique du scan du lecteur USB HID-keyboard
    useEffect(() => {
        if (!selectedUser)
            return;
        badgeInputRef.current?.focus();
    }, [selectedUser]);
    const statutBadge = (statut) => {
        const config = {
            "En attente": { color: "warning", icon: _jsx(ClockRegular, {}), label: "En attente" },
            "En cours": { color: "informative", icon: _jsx(ArrowSyncRegular, {}), label: "En cours" },
            "Traite": { color: "success", icon: _jsx(CheckmarkCircleRegular, {}), label: "Traité" },
            "Erreur": { color: "danger", icon: _jsx(ErrorCircleRegular, {}), label: "Erreur" },
        };
        const c = config[statut] || config["En attente"];
        return _jsx(Badge, { appearance: "filled", color: c.color, icon: c.icon, children: c.label });
    };
    const formatDate = (iso) => {
        if (!iso)
            return "—";
        return new Date(iso).toLocaleString("fr-FR", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });
    };
    const enAttente = enrollements.filter((e) => e.statut === "En attente" || e.statut === "En cours");
    const traites = enrollements.filter((e) => e.statut === "Traite");
    const erreurs = enrollements.filter((e) => e.statut === "Erreur");
    return (_jsxs("div", { children: [_jsxs("div", { style: { marginBottom: "16px" }, children: [_jsx(Text, { size: 500, weight: "bold", children: "Enr\u00F4lement badge" }), _jsx("br", {}), _jsx(Text, { size: 200, style: { color: "#666" }, children: "Associer un badge RFID \u00E0 un nouveau salari\u00E9 (cr\u00E9\u00E9 dans Entra mais absent de Kelio)" })] }), error && _jsx(MessageBar, { intent: "error", style: { marginBottom: "12px" }, children: _jsx(MessageBarBody, { children: error }) }), success && _jsx(MessageBar, { intent: "success", style: { marginBottom: "12px" }, children: _jsx(MessageBarBody, { children: success }) }), _jsxs(Card, { style: { marginBottom: "16px", padding: "16px" }, children: [_jsxs("div", { style: { display: "flex", alignItems: "center", gap: "8px", marginBottom: "16px" }, children: [_jsx(TagRegular, { style: { fontSize: 24, color: "#0078d4" } }), _jsx(Text, { weight: "semibold", size: 400, children: "Nouveau badge" })] }), _jsxs("div", { style: { display: "grid", gridTemplateColumns: "1fr 1fr", gap: "16px" }, children: [_jsx(Field, { label: "1. S\u00E9lectionner le salari\u00E9", required: true, children: _jsx(PeoplePicker, { accessToken: graphToken || "", selectedName: selectedUser?.displayName || "", selectedEmail: selectedUser?.mail || "", onSelect: handleUserSelect, onClear: () => { setSelectedUser(null); setBadgeUid(""); }, placeholder: "Rechercher le nouveau salari\u00E9..." }) }), _jsx(Field, { label: "2. Scanner le badge RFID", required: true, hint: selectedUser ? "Passez le badge devant le lecteur, l'UID apparaîtra automatiquement" : "Sélectionnez d'abord un salarié", children: _jsx(Input, { ref: badgeInputRef, value: badgeUid, onChange: (_, d) => handleBadgeScan(d.value), placeholder: "UID du badge (ex: 0011223344)", disabled: !selectedUser, contentBefore: _jsx(TagRegular, {}), style: { fontFamily: "monospace", fontSize: "14px" } }) }), _jsx(Field, { label: "Service (pr\u00E9-rempli depuis Entra)", children: _jsx(Input, { value: serviceCible, onChange: (_, d) => setServiceCible(d.value), placeholder: "Service du salari\u00E9", disabled: !selectedUser }) }), _jsx(Field, { label: "Manager N+1 (pr\u00E9-rempli depuis Entra)", children: _jsx(Input, { value: managerEmail, onChange: (_, d) => setManagerEmail(d.value), placeholder: "email@avh.asso.fr", disabled: !selectedUser }) }), _jsx(Field, { label: "Horaire Kelio", children: _jsx(Dropdown, { value: horaire, selectedOptions: [horaire], onOptionSelect: (_, d) => setHoraire(d.optionValue || "HORAIRE 35H"), disabled: !selectedUser, children: HORAIRES_DEFAUT.map((h) => _jsx(Option, { value: h, children: h }, h)) }) }), _jsx(Field, { label: "Profil badgeage", children: _jsx(Dropdown, { value: profil, selectedOptions: [profil], onOptionSelect: (_, d) => setProfil(d.optionValue || "Badger ses entrées/sorties"), disabled: !selectedUser, children: PROFILS_DEFAUT.map((p) => _jsx(Option, { value: p, children: p }, p)) }) })] }), _jsxs("div", { style: { marginTop: "16px", display: "flex", justifyContent: "flex-end", gap: "8px" }, children: [_jsx(Button, { appearance: "subtle", onClick: () => {
                                    setSelectedUser(null);
                                    setBadgeUid("");
                                    setServiceCible("");
                                    setManagerEmail("");
                                }, disabled: submitting || !selectedUser, children: "Annuler" }), _jsx(Button, { appearance: "primary", icon: submitting ? _jsx(Spinner, { size: "tiny" }) : _jsx(SendRegular, {}), onClick: handleSubmit, disabled: !selectedUser || !badgeUid || submitting, children: submitting ? "Envoi..." : "Soumettre" })] })] }), _jsxs("div", { style: { display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: "24px", marginBottom: "12px" }, children: [_jsx(Text, { size: 400, weight: "semibold", children: "File d'attente" }), _jsxs("div", { style: { display: "flex", gap: "8px", alignItems: "center" }, children: [_jsxs(Badge, { appearance: "tint", color: "warning", children: [enAttente.length, " en attente"] }), _jsxs(Badge, { appearance: "tint", color: "success", children: [traites.length, " trait\u00E9s"] }), erreurs.length > 0 && _jsxs(Badge, { appearance: "filled", color: "danger", children: [erreurs.length, " erreurs"] }), _jsx(Button, { icon: _jsx(ArrowSyncRegular, {}), appearance: "subtle", size: "small", onClick: fetchData })] })] }), loading ? _jsx(Spinner, { label: "Chargement..." }) : enrollements.length === 0 ? (_jsx(Text, { style: { color: "#9ca3af", padding: "24px", display: "block", textAlign: "center" }, children: "Aucune demande d'enr\u00F4lement" })) : (_jsxs(Table, { size: "small", children: [_jsx(TableHeader, { children: _jsxs(TableRow, { children: [_jsx(TableHeaderCell, { children: "Salari\u00E9" }), _jsx(TableHeaderCell, { children: "Badge UID" }), _jsx(TableHeaderCell, { children: "Service" }), _jsx(TableHeaderCell, { children: "Horaire" }), _jsx(TableHeaderCell, { children: "Demand\u00E9 par" }), _jsx(TableHeaderCell, { children: "Date" }), _jsx(TableHeaderCell, { children: "Statut" })] }) }), _jsx(TableBody, { children: enrollements.map((e) => (_jsxs(TableRow, { children: [_jsxs(TableCell, { children: [_jsx(Text, { weight: "semibold", children: e.employeeName }), _jsx("br", {}), _jsx(Text, { size: 200, style: { color: "#666" }, children: e.employeeEmail })] }), _jsx(TableCell, { children: _jsx(Text, { size: 200, style: { fontFamily: "monospace" }, children: e.badgeUid }) }), _jsx(TableCell, { children: e.serviceCible || "—" }), _jsx(TableCell, { children: _jsx(Badge, { appearance: "outline", size: "small", children: e.horaireCible || "—" }) }), _jsx(TableCell, { children: _jsx(Text, { size: 200, children: e.demandePar }) }), _jsx(TableCell, { children: _jsx(Text, { size: 200, children: formatDate(e.createdAt) }) }), _jsxs(TableCell, { children: [statutBadge(e.statut), e.erreurDetail && (_jsx("div", { style: { marginTop: "4px" }, children: _jsx(Text, { size: 100, style: { color: "#dc2626" }, children: e.erreurDetail }) }))] })] }, e.id))) })] }))] }));
};
